'use client';

import { useState, useEffect } from 'react';
import { Col } from 'react-bootstrap';
import CaseStudyComponent from '../caseStudyComponent/caseStudyComponent';
import { imageData } from '@/config/defaultBlur';
import { SearchCaseStudiesByTitleResult } from '@/sanity.types';
import BlogComponentSkeleton from '@/components/skeleton/blogComponentSkeleton';
import { isValidSearchQuery, sanitizeSearchTerm } from '@/util/searchUtils';

interface SearchResultsCaseStudiesProps {
  searchQuery: string;
  onSearchComplete?: (hasResults: boolean) => void;
}

const SearchResultsCaseStudies = ({
  searchQuery,
  onSearchComplete,
}: SearchResultsCaseStudiesProps) => {
  const [searchResults, setSearchResults] =
    useState<SearchCaseStudiesByTitleResult>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sanitizedQuery, setSanitizedQuery] = useState<string>('');

  useEffect(() => {
    const performSearch = async () => {
      // Clean up the search query
      const cleanQuery = searchQuery?.trim() || '';

      // Store the sanitized version for display purposes
      const sanitized = sanitizeSearchTerm(cleanQuery);
      setSanitizedQuery(sanitized);

      if (!cleanQuery) {
        setSearchResults([]);
        onSearchComplete?.(false);
        return;
      }

      // Use shared validation function
      if (!isValidSearchQuery(cleanQuery)) {
        setSearchResults([]);
        onSearchComplete?.(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await fetch(
          `/api/search/case-studies?searchTerm=${encodeURIComponent(sanitized)}`
        );

        if (!response.ok) {
          throw new Error('Failed to fetch search results');
        }

        const results = await response.json();

        setSearchResults(results || []);
        onSearchComplete?.(results && results.length > 0);
      } catch (err) {
        console.error('Search error:', err);
        setError('Failed to search case studies. Please try again.');
        setSearchResults([]);
        onSearchComplete?.(false);
      } finally {
        setLoading(false);
      }
    };

    performSearch();
  }, [searchQuery, onSearchComplete]);

  if (loading) {
    return (
      <Col lg={12}>
        <div className="d-flex justify-content-center p-4">
          <span className="visually-hidden">Searching...</span>
          <BlogComponentSkeleton />
        </div>
      </Col>
    );
  }

  if (error) {
    return (
      <Col lg={12}>
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </Col>
    );
  }

  if (searchQuery && searchResults.length === 0) {
    const validQuery = isValidSearchQuery(searchQuery);
    return (
      <Col lg={12}>
        <div className="text-center p-4">
          <h3>No results found</h3>
          {validQuery ? (
            <p>
              No case studies found for &quot;{sanitizedQuery || searchQuery}
              &quot;. Try different keywords.
            </p>
          ) : (
            <p>
              Please enter at least 3 characters using letters or numbers to
              search for blog posts.
            </p>
          )}
        </div>
      </Col>
    );
  }

  return (
    <Col lg={12}>
      <div className="search-results-header my-3">
        <h3 id="search-results-case-studies">
          Search Results for &quot;{sanitizedQuery}&quot;
        </h3>
        <p className="text-muted">{searchResults.length} result(s) found</p>
      </div>
      <div
        role="region"
        aria-labelledby="search-results-case-studies"
        className="caseStudyCardContainer"
        aria-live="polite"
      >
        {searchResults.map((post: SearchCaseStudiesByTitleResult[0]) => {
          const title = post?.title;
          const caseStudyImage = post?.coverImage;
          const caseStudyImageAlt = post?.coverImage?.alt;

          return (
            <CaseStudyComponent
              src={caseStudyImage || '/n_images/default-blog.jpg'}
              alt={caseStudyImageAlt || ''}
              caseStudyTitle={title || ''}
              href={`/case-studies/${post?.slug?.current}`}
              key={post?._id}
              blurDataURL={
                post?.coverImage?.imgAsset?.metadata?.lqip ||
                imageData.blurImage
              }
            />
          );
        })}
      </div>
    </Col>
  );
};

export default SearchResultsCaseStudies;
